/**
 * WHICH SUBJECTS THE ORDINARY APPLICATION PALETTE MAY REACH (ROOT ruling 74071 §5).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ THE DECLARATION BELOW IS INTERIM. THE AUTHORITY IS CORE, AND IT IS OWED. ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Ruling 74071 §5, verbatim: *"Administrative/settings entities and their
 * records must not be queried or returned by the ordinary application palette.
 * Add a governed pre-query classification for providers/entities; do not use
 * `navigation_eligible`, do not hand-maintain a React blacklist, and do not
 * query then string-filter."*
 *
 * ── WHAT WAS MEASURED, AND WHY "NOT QUERIED" IS THE HARD WORD ──────────────
 * `CommandPalette.tsx` asked `describe_tools()` for every read-only rpc-bound
 * `search_*` tool and handed the WHOLE list to `runFederatedSearch`, which fans
 * out on every keystroke. The administrative doors ROOT's census named —
 * `core.search_retention_policies`, `access.search_users`,
 * `core.search_audit_redactions`, `core.search_automation_principals`,
 * `approvals.search_templates`, `tenders.search_authority_policy` — were all in
 * that fan-out. The hits were then dropped at render time by
 * `canOpenPaletteRecord`. So the door was ASKED and the answer thrown away,
 * which is exactly the shape the ruling forbids: a refusal that happens after
 * the question reached the database leaks through latency, through the
 * *"searched N of M governed sources"* count, and through the server's own logs
 * — and it is one `if` away from leaking through the results.
 *
 * ── WHY THIS IS CORE-SHAPED AND NOT A CONSOLE JUDGEMENT ────────────────────
 * The subject set is OPEN. Measured on this tree: **109 registered search MCP
 * tools** across 34 modules, and `core.entity_type` carries hundreds of
 * entities — with a customer module this console has never heard of being the
 * normal case, not the exception. A console-side list therefore cannot BE the
 * property; it can only be a selector narrower than it, which skill rule 26
 * records as indistinguishable from compliance.
 *
 * Nor can it be derived from anything already on the wire:
 *   · `navigation_eligible` is forbidden BY THE RULING, and its own migration
 *     (`2026091009:41-49`) says why — it answers *"is this a record?"*;
 *   · `non_record_surface` is the same axis one field over;
 *   · `default_visibility = 'reference'` is a VISIBILITY decision that country
 *     lists and currencies share while being nothing like administrative;
 *   · module `audience` answers *"may a customer box install it?"*.
 *
 * The contract that does answer it is reproduced at the foot of this file, so a
 * reader here can see what replaces this declaration without leaving the module.
 *
 * ── SO WHAT IS DECLARED HERE, AND WHAT IT IS NOT ───────────────────────────
 * `ADMINISTRATIVE_SUBJECTS` is a MEASURED declaration, not an invented one:
 * every row carries the census line or the registering migration it came from,
 * and the administrative SURFACE that owns it. Thirty-odd rows against an open
 * set is a floor, and it is written as one — `classifyPaletteSubject` reports
 * the SOURCE of every answer, so a caller can tell a governed classification
 * from a declared one from the residual default, and `providers.ts` records
 * which subjects it excluded and why.
 *
 * It is not the forbidden React blacklist, and the difference is structural
 * rather than rhetorical:
 *   · it is ONE module, not a condition inside a component;
 *   · every row names the administrative surface it belongs to, and
 *     `paletteClass.parity.test.ts` binds those surfaces to the Settings
 *     Studio's own destination authority in both directions — so a surface that
 *     leaves the Studio cannot leave a stale exclusion behind, and a row cannot
 *     name a surface this console does not draw;
 *   · the governed answer OUTRANKS it the moment a deployment compiles one, and
 *     a governed answer that CONTRADICTS a declared row raises rather than
 *     quietly winning, because two authorities disagreeing about whether a door
 *     is administrative is a fact somebody has to see.
 *
 * ── TOTAL, AND WHAT IT RAISES ON ───────────────────────────────────────────
 * `classifyPaletteSubject` is TOTAL: it answers for every subject, always with a
 * class, a source and a reason. It never returns `undefined` and it has no
 * "unknown" arm, because an unknown arm is where a caller invents a default.
 *
 * It RAISES on the DECLARATION's integrity rather than on an unclassified
 * subject, and that is a deliberate difference from `classifyPlatformWorkspace`
 * (`settings/platformWorkspaces.ts`), whose domain is fourteen declared routes
 * and can therefore raise on absence. This domain is the fleet's entity and tool
 * set. A classifier that raised on the first entity it had never heard of would
 * refuse to open the palette on every deployment carrying a customer module —
 * i.e. on the deployments this platform exists to serve. So the raises are:
 *
 *   · a duplicate subject key — two rows for one door cannot both be the reason;
 *   · a subject key that is not a stable key shape;
 *   · a row whose surface is not in the declared surface vocabulary;
 *   · a governed class that contradicts a declared row.
 *
 * and the DEFAULT for an undeclared subject is `business`, said out loud here
 * rather than left to be discovered: fail-closed would empty the palette on
 * every deployment that predates the governed column, and the palette is this
 * console's primary navigation. That default IS the residual gap, it is reported
 * to the core lane, and it closes when the column lands.
 *
 * ── MUTATION (skill rule 10) — the table is in `paletteClass.test.ts` ──────
 */

/** The two classes. A subject is exactly one of them. */
export type PaletteClass = 'business' | 'administrative'

/**
 * What kind of thing is being classified.
 *
 * Two, because the ruling names two — *"providers/entities"* — and they are
 * keyed differently. A `search-door` is keyed by its GLOBAL MCP tool name, which
 * `core.register_mcp_tool` enforces as globally unique (core `2026081116`: *"an
 * MCP tool name is the wire contract an agent calls … so it is globally
 * unique"*), and that enforcement is what makes the name a legitimate stable key
 * rather than a string somebody hopes is distinct.
 */
export type PaletteSubjectKind = 'entity' | 'search-door'

/** One thing the palette is about to reach for. */
export interface PaletteSubject {
  kind: PaletteSubjectKind
  /** The entity key, or the global tool name. */
  key: string
  /**
   * The GOVERNED classification, when the deployment compiled one.
   *
   * `undefined` means the deployment predates the column — which is every
   * deployment today. `null` is the same answer given by a deployment that HAS
   * the column and left it unset, and the two are deliberately not collapsed:
   * one is an old box, the other is a module that has not declared, and only the
   * second is somebody's omission.
   */
  governed?: PaletteClass | null
}

/** The answer, with where it came from and why. */
export interface PaletteClassification {
  paletteClass: PaletteClass
  /**
   *  `governed`  the deployment's compiled authority answered.
   *  `declared`  `ADMINISTRATIVE_SUBJECTS` below answered — interim.
   *  `default`   nothing answered, and the residual default applied.
   */
  source: 'governed' | 'declared' | 'default'
  reason: string
}

/**
 * The administrative surfaces this console draws, as the ADDRESS each is
 * reached at.
 *
 * Addresses rather than names, because an address is what
 * `paletteClass.parity.test.ts` can compare against the Settings Studio's own
 * destination authority (`studioWorkspaceDestinations()` and
 * `settingsDestinations()`). A name would have to be matched by eye.
 *
 * `null` is a real member of this vocabulary and carries a measurement: three of
 * the six doors ROOT's census named — retention policies, audit redactions and
 * automation principals — have NO console destination at all. Measured against
 * `router.tsx`: there is no retention, audit or automation route, and no
 * `/settings` tab for any of them. Their only console reach today was the ⌘K
 * fan-out this file removes them from, which is worth knowing: an administrative
 * subject with no surface is not one the palette should compensate for by
 * listing it.
 */
export const ADMINISTRATIVE_SURFACES = [
  '/access',
  '/personas',
  '/approvals',
  '/flows',
  '/rules',
  '/modules',
  '/templates',
  '/settings?tab=entities',
  '/settings?tab=relationships',
  '/settings?tab=party-roles',
  '/settings?tab=legal-entities',
  '/settings?tab=billing',
  null,
] as const

export type AdministrativeSurface = typeof ADMINISTRATIVE_SURFACES[number]

/** One declared administrative subject. */
export interface AdministrativeSubject {
  kind: PaletteSubjectKind
  key: string
  /** The console surface that administers it, or `null` — see the vocabulary. */
  surface: AdministrativeSurface
  /** Why it is administrative, and where that was measured. */
  reason: string
}

/** The stable key shape both kinds share. */
const SUBJECT_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/

/**
 * The declaration. Every row is a measurement with its source named.
 *
 * Ordered by administrative surface so the shape of the list is legible: it is
 * this console's own administration, read as the things each surface administers.
 */
export const ADMINISTRATIVE_SUBJECTS: readonly AdministrativeSubject[] = [
  /* ── /access — People & Access (ruling §6's `identity` category) ────────── */
  {
    kind: 'search-door', key: 'search_users', surface: '/access',
    reason: 'ROOT 74071 §5 census: access.search_users was fanned out on every keystroke. '
      + 'Its own registration calls it "Platform administrators only" (access 2026090203:324)',
  },
  { kind: 'entity', key: 'core_app_user', surface: '/access', reason: 'the sign-in identity People & Access administers, not a person in the directory' },
  { kind: 'entity', key: 'core_role', surface: '/access', reason: 'a privilege definition; roles gate what may be done, they are not work' },
  { kind: 'entity', key: 'core_user_role', surface: '/access', reason: 'the grant edge itself — who holds which role, administered at People & Access' },
  {
    kind: 'entity', key: 'access_idp_connection', surface: '/access',
    reason: 'identity-federation configuration; access.search_idp_connection is the door onto it',
  },
  {
    kind: 'search-door', key: 'search_idp_connection', surface: '/access',
    reason: 'the door onto access_idp_connection, administered at the same People & Access surface',
  },

  /* ── /personas — Personas (ruling §6's `navigation` category) ──────────── */
  {
    kind: 'entity', key: 'core_persona', surface: '/personas',
    reason: 'ROOT 74071 §5 census: typing "persona" returned an administrative entity destination',
  },

  /* ── /approvals — Approvals configuration.
       The INBOX is business work and is deliberately absent below: ruling §6
       keeps /approvals/inbox in business navigation, so approvals_task and
       approvals_instance are a queue of decisions somebody owes and belong in
       the ordinary palette. Excluding them would hide work. ──────────────── */
  {
    kind: 'search-door', key: 'search_templates', surface: '/approvals',
    reason: 'ROOT 74071 §5 census: approvals.search_templates searches process DEFINITIONS (approvals 0010:147)',
  },
  { kind: 'entity', key: 'approvals_process_definition', surface: '/approvals', reason: 'the approval template a policy instantiates; the INBOX beside it stays business work' },
  { kind: 'entity', key: 'approvals_decision_table', surface: '/approvals', reason: 'the routing rules an approval definition evaluates — configuration, not a decision owed' },
  { kind: 'entity', key: 'approvals_group', surface: '/approvals', reason: 'who an approval stage resolves to, declared once as configuration rather than per request' },

  /* ── /flows and /rules — authored behaviour (ruling §6's `operations`) ──── */
  { kind: 'search-door', key: 'search_flows', surface: '/flows', reason: 'the door onto authored automation, administered by the Flows workspace' },
  { kind: 'entity', key: 'core_flow', surface: '/flows', reason: 'authored automation, designed and put live from the Flows workspace' },
  { kind: 'entity', key: 'core_flow_instance', surface: '/flows', reason: 'one run of an authored flow — operational evidence about configuration' },
  { kind: 'entity', key: 'core_flow_simulation', surface: '/flows', reason: 'a rehearsal of an authored flow, produced while designing it' },
  { kind: 'entity', key: 'core_rule', surface: '/rules', reason: 'business policy authored as data, on the same shelf as Flows and for the same reason' },

  /* ── /modules — the installed composition (ruling §6's `environments`) ─── */
  { kind: 'entity', key: 'core_module', surface: '/modules', reason: 'the installed composition of this environment, administered at the Modules workspace' },

  /* ── /templates — Template Studio (ruling §6's `experience`) ───────────── */
  { kind: 'entity', key: 'core_template', surface: '/templates', reason: 'how this deployment presents itself in documents and mail, authored in Template Studio' },
  { kind: 'entity', key: 'core_template_revision', surface: '/templates', reason: 'one version of an authored template, kept by Template Studio' },
  { kind: 'entity', key: 'core_template_style', surface: '/templates', reason: 'the styling an authored template renders with, governed by Template Studio' },

  /* ── the settings tabs that administer the metamodel ───────────────────── */
  { kind: 'entity', key: 'core_entity_type', surface: '/settings?tab=entities', reason: 'the metamodel itself — what entities exist, edited on the Entities & fields page' },
  { kind: 'entity', key: 'core_field_meta', surface: '/settings?tab=entities', reason: 'the field dictionary that decides how every surface draws a column' },
  { kind: 'entity', key: 'core_relationship_type', surface: '/settings?tab=relationships', reason: 'what may be linked to what — the relationship grammar the Relationships page edits' },
  { kind: 'entity', key: 'core_party_role', surface: '/settings?tab=party-roles', reason: 'the party-role vocabulary the Party roles settings page edits as configuration' },
  {
    kind: 'entity', key: 'core_legal_entity', surface: '/settings?tab=legal-entities',
    reason: 'the deployment\'s own supplier/issuer identity (core 2026102207), steward-configured on the Legal entities page — configuration, not work',
  },
  {
    kind: 'search-door', key: 'core_search_legal_entity', surface: '/settings?tab=legal-entities',
    reason: 'the door onto core_legal_entity (core 2026102207), administered at the same Legal entities settings page',
  },
  {
    kind: 'entity', key: 'billing_issuer_profile', surface: '/settings?tab=billing',
    reason: 'an issuer\'s financial configuration (billing 2026102510, #2756 3C) — finance-configured on the Billing settings page\'s Issuers section, configuration rather than work',
  },
  {
    kind: 'search-door', key: 'billing_search_issuer_profile', surface: '/settings?tab=billing',
    reason: 'the door onto billing_issuer_profile (billing 2026102510), administered at the same Billing settings page',
  },

  /* ── NO CONSOLE SURFACE, and the measurement is the point: router.tsx has no
       retention, audit or automation route, so the palette fan-out was their
       only console reach. ─────────────────────────────────────────────────── */
  {
    kind: 'search-door', key: 'search_retention_policies', surface: null,
    reason: 'ROOT 74071 §5 census. Retention is a governed obligation administered through core 0126 doors; no console route exists',
  },
  { kind: 'entity', key: 'core_retention_policy', surface: null, reason: 'the retention policy that door searches — a governed obligation, not a record of work' },
  { kind: 'entity', key: 'core_retention_run', surface: null, reason: 'the execution evidence of a retention policy, produced by the platform and not by anyone' },
  {
    kind: 'search-door', key: 'search_audit_redactions', surface: null,
    reason: 'ROOT 74071 §5 census. An audit redaction is evidence about an erasure (core 0125); no console route exists',
  },
  { kind: 'entity', key: 'core_audit_redaction', surface: null, reason: 'the redaction record that door searches — evidence about an erasure that already happened' },
  {
    kind: 'search-door', key: 'search_automation_principals', surface: null,
    reason: 'ROOT 74071 §5 census. An automation principal is an identity whose GRANTS decide what a flow may do (core 2026081001:1202)',
  },
  { kind: 'entity', key: 'core_automation_principal', surface: null, reason: 'the automation principal that door searches; its grants decide what a flow may do' },
  {
    kind: 'search-door', key: 'search_authority_policy', surface: null,
    reason: 'ROOT 74071 §5 census: tenders.search_authority_policy searches WHO MAY APPROVE a bid, not a bid (tenders 0011:1085)',
  },
  { kind: 'entity', key: 'tenders_authority_policy', surface: null, reason: 'the bid authority policy that door searches — who may approve, never what is being bid' },
] as const

/**
 * The declaration, indexed — and every integrity raise, once, at first use.
 *
 * Built lazily and memoised rather than at module scope, because a module that
 * throws while being imported takes the whole console down with a stack trace
 * that names an import and not a cause.
 */
let indexed: Map<string, AdministrativeSubject> | null = null

/**
 * Index a declaration, running every integrity guard over it — EXPORTED so the
 * guards can be DRIVEN rather than described.
 *
 * It is exported because of a mutation run. Three arms that turned these guards
 * off came back GREEN: the tests had re-implemented the duplicate check, the
 * surface check and the key-shape check locally, over the SHIPPED declaration,
 * and were therefore asserting that the shipped rows are well formed — true, and
 * nothing at all about the guard. Skill rule 10 is explicit that such a test is
 * worse than none, because it certifies protection that is not there.
 *
 * So the test now calls THIS, with deliberately bad rows, and a guard turned off
 * reddens by name. The shipped declaration goes through the identical path via
 * `subjectIndex()` below — one implementation, two callers, no second copy of
 * the rules to drift. The memoisation is why this had to be extracted rather
 * than simply called: the shipped index is built once and cached, so a test
 * could never reach the guards a second time with different input.
 */
export function buildSubjectIndex(
  subjects: readonly AdministrativeSubject[],
  surfaceVocabulary: readonly AdministrativeSurface[] = ADMINISTRATIVE_SURFACES,
): Map<string, AdministrativeSubject> {
  const surfaces = new Set<AdministrativeSurface>(surfaceVocabulary)
  const map = new Map<string, AdministrativeSubject>()
  for (const subject of subjects) {
    if (!SUBJECT_KEY.test(subject.key)) {
      throw new Error(
        `ADMINISTRATIVE_SUBJECTS: "${subject.key}" is not a stable subject key — expected ${String(SUBJECT_KEY)}`,
      )
    }
    if (!surfaces.has(subject.surface)) {
      throw new Error(
        `ADMINISTRATIVE_SUBJECTS: "${subject.key}" names the surface ${JSON.stringify(subject.surface)}, `
        + 'which is not in ADMINISTRATIVE_SURFACES. A row pointing at a surface this console does not draw '
        + 'is an exclusion nobody can check against the Settings Studio.',
      )
    }
    const id = `${subject.kind}:${subject.key}`
    if (map.has(id)) {
      throw new Error(
        `ADMINISTRATIVE_SUBJECTS: "${id}" is declared twice — two rows for one subject `
        + 'cannot both be the reason it is excluded',
      )
    }
    map.set(id, subject)
  }
  return map
}

/** The shipped declaration, indexed once. Memoised; built by the function above. */
function subjectIndex(): Map<string, AdministrativeSubject> {
  if (indexed === null) indexed = buildSubjectIndex(ADMINISTRATIVE_SUBJECTS)
  return indexed
}

/**
 * The classification of one subject. TOTAL — see the file header for what it
 * raises on and, more importantly, for what it deliberately does not.
 */
export function classifyPaletteSubject(subject: PaletteSubject): PaletteClassification {
  const declared = subjectIndex().get(`${subject.kind}:${subject.key}`)
  const governed = subject.governed

  if (governed === 'business' || governed === 'administrative') {
    /* THE TWO AUTHORITIES MAY NOT DISAGREE SILENTLY. A governed "business" on a
       subject this console declares administrative is either a module that
       classified wrongly or a declaration that has gone stale, and both are
       facts a person has to see. Winning quietly would make the interim list
       look correct right up until the day it was deleted. */
    if (declared !== undefined && governed !== 'administrative') {
      throw new Error(
        `classifyPaletteSubject("${subject.kind}:${subject.key}"): the deployment classifies it `
        + `"${governed}" while ADMINISTRATIVE_SUBJECTS declares it administrative (${declared.reason}). `
        + 'Resolve the disagreement — either the module classification is wrong, or this row is stale '
        + 'and should be deleted now that the governed authority answers.',
      )
    }
    return {
      paletteClass: governed,
      source: 'governed',
      reason: 'the compiled classification of this deployment answered',
    }
  }

  if (declared !== undefined) {
    return { paletteClass: 'administrative', source: 'declared', reason: declared.reason }
  }

  return {
    paletteClass: 'business',
    source: 'default',
    reason: 'no governed classification and no declared row — see this module header for the residual gap',
  }
}

/** Convenience for the common question, with the reasoning discarded. */
export function isAdministrative(subject: PaletteSubject): boolean {
  return classifyPaletteSubject(subject).paletteClass === 'administrative'
}

/**
 * THE CORE CONTRACT THIS FILE IS A STAND-IN FOR (ruling 74071 §5), reproduced
 * here so a reader does not have to find the lane report.
 *
 * Reported, not built: this lane claims no core rung and no migration stamp
 * (#2323 holds 0.368.0, #2324 holds 0.369.0).
 *
 *   -- a finite governed vocabulary, registered as a REFERENCE SET rather than a
 *   -- CHECK (skill rule 44), so a deployment can see what the values mean:
 *   core.register_reference_set('palette_class', …, '{business,administrative}')
 *
 *   alter table core.entity_type add column palette_class text   -- null = undeclared
 *   alter table core.mcp_tool    add column palette_class text
 *
 *   -- declared by the module that registers the entity or the tool, as a PRESET
 *   -- the deployment may override (skill rule 44):
 *   core.register_entity(..., p_palette_class => 'administrative')
 *   core.register_mcp_tool(..., p_palette_class => 'administrative')
 *
 *   -- and PROJECTED, so the client half above reads it with no second call:
 *   core.describe_schema()  -> entity.palette_class
 *   core.describe_tools()   -> tool.palette_class
 *
 * Two properties the SQL half must hold that this file cannot:
 *
 *  · NULL IS NOT `business`. The column's absence is why `classify` defaults to
 *    business today; once it exists, an UNDECLARED entity should be reported by
 *    a fleet ratchet (the `module-dod` shape) rather than silently admitted, so
 *    that the default stops being load-bearing.
 *  · THE CLASSIFICATION IS NOT AUTHORIZATION. #2328 §8: *"UI omission is not
 *    authority."* Every administrative door still authorizes its own read —
 *    `access.search_users` refuses a non-administrator whether or not the
 *    palette asked it. This column decides what the palette REACHES FOR, which
 *    is a findability question, and it must never become the only gate.
 */
